import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import { Dropdown, Menu, MenuProps } from "antd";
import { IS_AWS_ENV } from "constants/app.constants";
import { EvaChart, RigFracActivityMetric, RigFracGroupBy } from "constants/charts.enums";
import { useChartsContext } from "contexts/ChartContext";
import { DEFAULT_RIG_FRAC_ACTIVITY_REQUEST } from "entities/charts/eva/requests/rigFracActivity";
import { resolveRigFracMetrics } from "entities/charts/eva/requests/rigFracMetrics";
import { DEFAULT_RIG_FRAC_RECENT_ACTIVITY_REQUEST } from "entities/charts/eva/requests/rigFracRecentActivity";
import omit from "lodash/omit";
import { updateChart } from "store/features/charts/chartSlice";
import { ChartRequestType, RigFracActivityChartRequestType } from "types";

import { useChartEntities } from "hooks/charts";

import ToolbarButton from "components/base/ToolbarButton";
import { GroupBy as GroupByIcon } from "components/icons";

import { RIG_FRAC_GROUP_BY_OPTIONS, isGroupByOfferedFor } from "./groupByOptions";

/**
 * Group-by dimension control for the Rig/Frac Activity chart (EVA-76 US3),
 * rendered inside RigFracChartToolbar as a labelled dropdown button styled like
 * DataSourceToggle. Writing the selection onto the entity request re-triggers
 * useRigFracActivityController's POST.
 */
function RigFracGroupBySelector() {
  const dispatch = useDispatch();
  const { id } = useChartsContext();
  const { request, chartType } = useChartEntities(id);
  const [open, setOpen] = useState(false);

  // Lean rig/frac request rides the entity's ChartRequestType-typed field —
  // same cast pattern as the controller hook.
  const selections = request as unknown as Maybe<
    Partial<RigFracActivityChartRequestType> & { metric?: RigFracActivityMetric }
  >;
  const value = selections?.groupBy ?? DEFAULT_RIG_FRAC_ACTIVITY_REQUEST.groupBy;
  const label = RIG_FRAC_GROUP_BY_OPTIONS.find((o) => o.value === value)?.label ?? value;

  // EVA-107: rig-count-only dimensions. Recent Activity carries a single
  // `metric`; Activity Over Time carries left/right metrics and the requested
  // gate is the LEFT axis (the bars), matching where rig count is chosen.
  // Mirrors RigFracMetricSelector's field resolution.
  const isRecent = chartType === EvaChart.RigFracRecentActivity;
  const resolved = resolveRigFracMetrics(selections);
  const activeMetric = isRecent ? resolved.metric : resolved.primaryMetric;

  const menuItems: MenuProps["items"] = useMemo(
    () =>
      RIG_FRAC_GROUP_BY_OPTIONS.filter(
        // Keep an already-applied dimension listed even once it stops being
        // offered, so the menu's selectedKeys still resolves and the user can
        // see — and change — what is actually applied.
        (o) =>
          (!o.awsOnly || IS_AWS_ENV || o.value === value) &&
          (isGroupByOfferedFor(activeMetric, o.value) || o.value === value)
      ).map((o) => ({ key: o.value, label: o.label })),
    [activeMetric, value]
  );

  const handleSelect: MenuProps["onClick"] = ({ key }) => {
    setOpen(false);
    const groupBy = key as RigFracGroupBy;
    if (groupBy === value) return;
    dispatch(
      updateChart({
        id,
        request: {
          // Seeded per chart type, so the other type's fields never reach a saved widget.
          ...(isRecent
            ? DEFAULT_RIG_FRAC_RECENT_ACTIVITY_REQUEST
            : DEFAULT_RIG_FRAC_ACTIVITY_REQUEST),
          // The focus bins belong to the old group-by.
          ...omit(selections, ["focusBins", "foldOthers"]),
          groupBy
        } as unknown as ChartRequestType
      })
    );
  };

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      // Hover-only, matching DataSourceToggle (the "source" button): hovering
      // opens the menu and CLICKING the button must not toggle it closed — with
      // a "click" trigger a click on the already-hover-opened button reads as a
      // toggle and dismisses the menu (EVA-76 QA).
      trigger={["hover"]}
      overlay={
        <Menu
          selectable
          selectedKeys={[value]}
          onClick={handleSelect}
          items={menuItems}
        />
      }>
      <ToolbarButton
        data-testid="rig-frac-group-by-trigger"
        aria-label={`Select Group By (${label})`}
        active={false}
        icon={<GroupByIcon size={18} />}
        overflowLabel={label}
        tooltipText="Select Group By"
      />
    </Dropdown>
  );
}

export default RigFracGroupBySelector;
